import { ItemCardDetails } from "@/components/ItemCard";
import { type Order } from "@repo/api";
import { Badge, LightMode } from "@repo/skapa";
import { t, useTranslation } from "@repo/translation";
import {
  localizeDateAndTime,
  translateOrderPaymentStatus,
  translateOrderStatus,
  translateOrderType,
  translateServiceStatus,
  useSettings,
} from "@repo/ui";
import { SimpleLabelField } from "@repo/ui";
import { hasItems } from "@repo/utils";

interface OrderCardDetailsProps {
  order: Order;
  isWaitingList?: boolean;
}

export const OrderCardDetails = ({
  order,
  isWaitingList,
}: OrderCardDetailsProps) => {
  const { dateFnsLocale } = useTranslation();
  const { kic_config, waiting_list } = useSettings();

  const installationServices = [
    "COMPLETE_KITCHEN_INSTALL",
    "KITCHEN_INSTALL_SPECIAL",
  ];
  const filtered =
    order?.providedServices?.filter((svc) =>
      installationServices.includes(svc?.serviceProductId)
    ) ?? [];

  const installationStatus =
    filtered.length === 0
      ? undefined
      : filtered.every((s) => s.status.toLowerCase() === "COMPLETED")
        ? "COMPLETED"
        : filtered.find((s) => s.status.toLowerCase() !== "COMPLETED")?.status;

  function getDeliveryCompleteness(order: Order) {
    const services = order.deliveryArrangements?.[0]?.deliveryServices ?? [];
    const total = services.length;
    const delivered = services.filter(
      (s) => s?.status.toLowerCase() === "delivered"
    ).length;

    return total > 0
      ? `${delivered} / ${total} ${t("service_status.Delivered")}`
      : "-";
  }

  const showDeliveryStatus =
    kic_config?.enabled && hasItems(order?.deliveryArrangements);
  const showInstallationStatus =
    kic_config?.enabled && hasItems(order?.providedServices);
  const showWaitingListStatus =
    waiting_list?.waiting_list_enabled && isWaitingList;

  return (
    <ItemCardDetails
      title={`${order.orderAlias ?? translateOrderType(order.orderType)} ${order.orderNo}`}
      description={`${t("label.created")}: ${localizeDateAndTime(order.orderCreationDate, dateFnsLocale)}`}
      addOn={
        <div>
          {showWaitingListStatus && (
            <Badge label={t("label.waiting_list")} size="small" colour="red" />
          )}
          <SimpleLabelField
            small
            label={t("label.total_articles")}
            value={order.items?.length ?? "-"}
            color={LightMode.colourTextAndIcon2}
          />
          <SimpleLabelField
            small
            label={t("label.type")}
            value={translateOrderType(order.orderType)}
            color={LightMode.colourTextAndIcon2}
          />
          <SimpleLabelField
            small
            label={t("label.status")}
            value={translateOrderStatus(order.orderStatus)}
            color={LightMode.colourTextAndIcon2}
          />
          {showDeliveryStatus && (
            <SimpleLabelField
              small
              label={t("label.delivery_status")}
              value={getDeliveryCompleteness(order)}
              color={LightMode.colourTextAndIcon2}
            />
          )}
          {showInstallationStatus && (
            <SimpleLabelField
              small
              label={t("label.installation_status")}
              value={translateServiceStatus(installationStatus)}
              color={LightMode.colourTextAndIcon2}
            />
          )}
          {order.orderType !== "ShoppingList" && (
            <SimpleLabelField
              small
              label={t("label.payment_status")}
              value={translateOrderPaymentStatus(
                order.paymentPlans?.[0]?.status
              )}
              color={LightMode.colourTextAndIcon2}
            />
          )}
          <SimpleLabelField
            small
            label={t("label.store_number")}
            value={order.buCode}
            color={LightMode.colourTextAndIcon2}
          />
        </div>
      }
    />
  );
};
